import React from 'react';
import styled from 'styled-components';
import {createFragmentContainer, graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {Button, NewTabIcon} from 'web-components';
import Util from '../../forecast-app/shared/util/util';
import CreateSisenseUserMutation from '../../mutations/CreateSisenseUserMutation';
import {hasModule} from '../../forecast-app/shared/util/ModuleUtil';
import {MODULE_TYPES, BUTTON_STYLE, BUTTON_COLOR} from '../../constants';
import {hasPermission} from '../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {showLoader, hideLoader} from '../../containers/global_loader';
import {TopHeaderBar, TopHeaderBarWrapper} from '../../forecast-app/shared/components/headers/top-header-bar/TopHeaderBar';
import EmptyState from '../../forecast-app/shared/components/empty-states/empty_state';
import {showModal, MODAL_TYPE} from '../../forecast-app/shared/components/modals/generic_modal_conductor';

const AdvancedAnalyticsPageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;
const AdvancedAnalyticsHeader = styled.div`
	flex: 0 0 68px;
`;
const AdvancedAnalyticsContent = styled.div`
	display: flex;
	flex-direction: row;
	align-self: stretch;
	flex: auto;
	border-top: 1px solid #e6e6ed;
`;
const SidePanel = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	flex: 0 0 333px;
	padding: 28px 35px 28px 35px;
	border-left: 1px solid #e6e6ed;
`;
const SidePanelSection = styled.div`
	font-family: Inter;
	display: flex;
	flex-direction: column;
	padding-top: ${props => props.paddingTop}px;
	color: #4c4c58;
	line-height: 20px;
`;
const SidePanelTitle = styled.div`
	font-weight: 600;
	font-size: 16px;
	margin-bottom: 12px;
`;
const SidePanelText = styled.div`
	font-size: 13px;
`;
const SidePanelDate = styled.div`
	font-size: 9px;
	line-height: 16px;
	letter-spacing: 1px;
`;
const DashboardsSection = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
	background: #f7f7fe;
	padding: 32px 26px 32px 32px;
`;
const DashboardsTitle = styled.div`
	font-family: Inter;
	font-style: normal;
	font-weight: 600;
	font-size: 16px;
	line-height: 20px;
	letter-spacing: 0.2px;
	color: #4c4c58;
	margin-bottom: 24px;
`;
const DashboardsContent = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	flex: auto;
	align-content: flex-start;
	gap: 16px;
`;
const Dashboard = styled.a`
	flex: 0 0 250px;
	max-width: 250px;
	height: 150px;
	background: white;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding: 16px 16px 20px 24px;
	box-shadow: 0px 3px 8px rgba(26, 26, 51, 0.11);
	border-radius: 6px;
	color: #4c4c58;
	font-family: Inter;
	cursor: pointer;
	text-decoration: none;
`;
const DashboardTitle = styled.div`
	font-weight: 600;
	font-size: 16px;
	line-height: 20px;
	letter-spacing: 0.2px;
	white-space: nowrap;
	overflow: hidden;
	max-width: 100%;
	text-overflow: ellipsis;
`;
const DashboardLastEdit = styled.div`
	font-size: 11px;
	line-height: 16px;
`;

export const AdvancedAnalyticsPage = ({viewer}) => {
	const intl = useIntl();

	const sendToSisense = () => {
		window.location.href =
			process.env.CIRCLE_BRANCH === 'production'
				? 'https://forecast.sisense.com/app/main#/home'
				: 'https://forecast-dev.sisense.com/app/main#/home';
	};

	const dashboardIds = {
		COO: '616830c369c07d0036ce1063',
		CFO1: '6177c047363c15003525449b',
		CFO2: '618be91d8e223e0035fd06e6',
		PD: '619b763a8e223e0035fd4b78',
	};

	const checkUser = () => {
		// Check if users is Sisense user
		if (viewer.sisenseUserId) {
			// Send to Sisense
			sendToSisense();
		} else {
			// Attempt to create Sisense user
			showLoader();
			const input = {
				email: viewer.email,
				firstName: viewer.firstName,
				lastName: viewer.lastName,
				type: hasPermission(PERMISSION_TYPE.SISENSE_CREATE) ? 'designer' : 'viewer',
			};
			const onSuccess = response => {
				hideLoader();
				const {errors} = response.createSisenseUser;
				if (errors?.length) {
					let errorMessage = '';
					if (errors.includes('NOT_ENOUGH_VIEWER_SEATS')) {
						errorMessage =
							'Your company does not have any more advanced analytics viewer seats. Please contact an administrator.';
					} else if (errors.includes('NOT_ENOUGH_DESIGNER_SEATS')) {
						errorMessage =
							'Your company does not have any more advanced analytics designer seats. Please contact an administrator.';
					} else if (errors.includes('ERROR_CREATING_USER')) {
						errorMessage =
							'An error occoured when trying to create a new user in Sisense. Please contact an administrator.';
					}
					showModal({
						type: MODAL_TYPE.WARNING,
						warningMessageId: 'common.error',
						warningInformation: [errorMessage],
						useInfoIcon: true,
						buttons: [
							{
								text: this.props.intl.formatMessage({id: 'common.ok'}),
								style: BUTTON_STYLE.FILLED,
								color: BUTTON_COLOR.WHITE,
							},
						],
					});
				} else {
					sendToSisense();
				}
			};
			const onError = () => {
				hideLoader();
				//TODO: Display error
			};
			Util.CommitMutation(CreateSisenseUserMutation, input, onSuccess, false, false, null, onError);
		}
	};

	const getDashboardHref = id => {
		return process.env.CIRCLE_BRANCH === 'production'
			? `https://forecast.sisense.com/app/main#/dashboards/${id}`
			: `https://forecast-dev.sisense.com/app/main#/dashboards/${id}`;
	};

	return hasModule(MODULE_TYPES.SISENSE) ? (
		<AdvancedAnalyticsPageWrapper>
			<AdvancedAnalyticsHeader>
				<TopHeaderBarWrapper>
					<TopHeaderBar title="Advanced Analytics" />
				</TopHeaderBarWrapper>
			</AdvancedAnalyticsHeader>
			<AdvancedAnalyticsContent>
				<DashboardsSection>
					{viewer.sisenseUserId ? (
						<>
							<DashboardsTitle>Dashboards</DashboardsTitle>
							<DashboardsContent>
								<Dashboard href={getDashboardHref(dashboardIds.COO)} target="_blank">
									<DashboardTitle>When to hire new resources</DashboardTitle>
									<DashboardLastEdit>{`Last edited ${intl.formatDate(new Date(2021, 11, 6), {
										day: 'numeric',
										month: 'short',
										year: 'numeric',
									})}`}</DashboardLastEdit>
								</Dashboard>
								<Dashboard href={getDashboardHref(dashboardIds.CFO1)} target="_blank">
									<DashboardTitle>Money left on the table</DashboardTitle>
									<DashboardLastEdit>{`Last edited ${intl.formatDate(new Date(2021, 11, 6), {
										day: 'numeric',
										month: 'short',
										year: 'numeric',
									})}`}</DashboardLastEdit>
								</Dashboard>
								<Dashboard href={getDashboardHref(dashboardIds.CFO2)} target="_blank">
									<DashboardTitle>Maximizing profitability</DashboardTitle>
									<DashboardLastEdit>{`Last edited ${intl.formatDate(new Date(2021, 11, 6), {
										day: 'numeric',
										month: 'short',
										year: 'numeric',
									})}`}</DashboardLastEdit>
								</Dashboard>
								<Dashboard href={getDashboardHref(dashboardIds.PD)} target="_blank">
									<DashboardTitle>How is my team performing</DashboardTitle>
									<DashboardLastEdit>{`Last edited ${intl.formatDate(new Date(2021, 11, 6), {
										day: 'numeric',
										month: 'short',
										year: 'numeric',
									})}`}</DashboardLastEdit>
								</Dashboard>
							</DashboardsContent>
						</>
					) : (
						<EmptyState pageName={EmptyState.EMPTY_STATE.SISENSE_NO_USER} />
					)}
				</DashboardsSection>
				<SidePanel>
					<Button
						onClick={checkUser}
						size={Button.SIZE.STANDARD}
						variant={Button.VARIANT.PURPLE_FILLED}
						iconPosition="last"
						icon={color => <NewTabIcon color={color} />}
					>
						Go to Analytics
					</Button>
					<SidePanelSection paddingTop={32}>
						<SidePanelTitle>Plan</SidePanelTitle>
						<SidePanelText>
							You are currently using
							<br />
							<b>{`${0} out of ${
								hasPermission(PERMISSION_TYPE.SISENSE_CREATE)
									? viewer.company.sisenseDesignerSeats
									: viewer.company.sisenseViewerSeats
							} avaliable seats.`}</b>
						</SidePanelText>
					</SidePanelSection>
					<SidePanelSection paddingTop={44}>
						<SidePanelTitle>About</SidePanelTitle>
						<SidePanelDate>06 Dec 2021</SidePanelDate>
						<SidePanelText>Advanced Analytics is now live!</SidePanelText>
					</SidePanelSection>
				</SidePanel>
			</AdvancedAnalyticsContent>
		</AdvancedAnalyticsPageWrapper>
	) : null;
};

export const AdvancedAnalyticsQuery = graphql`
	query AdvancedAnalyticsPage_Query {
		viewer {
			actualPersonId
			component(name: "advanced-analytics")
			...AdvancedAnalyticsPage_viewer
		}
	}
`;

export default createFragmentContainer(AdvancedAnalyticsPage, {
	viewer: graphql`
		fragment AdvancedAnalyticsPage_viewer on Viewer {
			sisenseUserId
			email
			firstName
			lastName
			availableFeatureFlags {
				key
			}
			company {
				sisenseViewerSeats
				sisenseDesignerSeats
			}
		}
	`,
});
